// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import {
	Alert,
	Grid,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
} from "@mui/material";
import { useFormContext } from "react-hook-form";
import { Add, Remove } from "@mui/icons-material";
import { v4 } from "uuid";
import { useKatex } from "../../../../../../helpers/hooks/useKatex";

interface ConfigRenderProps {
	prefix?: string;
}

interface AnswerConfig {
	label: string;
	xpos: number;
	ypos: number;
}

export default function ConfigRender({ prefix }: ConfigRenderProps) {
	const maxColumns = 3;
	const { setValue, getValues } = useFormContext();
	const [correctAnswer, setCorrectAnswer] = useState(getValues(`${prefix}correct_answer`) || []);
	const [answerConfig, setAnswerConfig] = useState(getValues(`${prefix}answer_config`) || []);

	const onChange = (value: string, index: number) => {
		setCorrectAnswer((prev) => {
			const newArr = [...prev];
			newArr[index] = value;
			setValue(`${prefix}correct_answer`, newArr);
			return newArr;
		});
	};

	const onConfigChange = (value: AnswerConfig, index: number) => {
		setAnswerConfig((prev) => {
			const newArr = [...prev];
			newArr[index] = value;
			setValue(`${prefix}answer_config`, newArr);
			return newArr;
		});
	};

	const [rows, setRows] = useState(
		new Array(Math.max(...getValues(`${prefix}answer_config`).map((x) => x.ypos || 1), 1)).fill(null).map(() => v4())
	);
	const [columns, setColumns] = useState(
		new Array(Math.max(...getValues(`${prefix}answer_config`).map((x) => x.xpos || 1), 1)).fill(null).map(() => v4())
	);
	const onAddColumns = () => {
		setColumns((prevState) => [...prevState, v4()]);
	};
	const onRemoveColumns = () =>
		setColumns((prevState) => {
			const array = [...prevState];
			array.pop();
			return array;
		});
	const onAddRows = () => setRows((prevState) => [...prevState, v4()]);
	const onRemoveRows = () =>
		setRows((prevState) => {
			const array = [...prevState];
			array.pop();
			return array;
		});

	return (
		<Grid item sx={{ maxWidth: "100%" }}>
			<Paper elevation={4} sx={{ marginBottom: "30px" }}>
				<TableContainer>
					<Table sx={{ minWidth: 650 }}>
						<TableHead>
							<TableRow>
								<TableCell
									colSpan={columns.length}
									padding={"none"}
									sx={{ borderRight: "1px solid #e0e0e0", backgroundColor: "#e5f6fd" }}
								>
									<Alert severity="info" sx={{ height: "auto" }}>
										Множественный ввод TeX-текста: Кнопка со знаком "+" добавит дополнительную колонку или строку
									</Alert>
								</TableCell>
								<TableCell padding={"none"} align={"center"}>
									<IconButton onClick={onAddColumns} disabled={maxColumns <= columns.length}>
										<Add />
									</IconButton>
									<IconButton onClick={onRemoveColumns} disabled={columns <= 1}>
										<Remove />
									</IconButton>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((key, i) => {
								return (
									<RenderConfigRow
										key={key}
										onChange={onChange}
										onConfigChange={onConfigChange}
										answerConfig={answerConfig}
										correctAnswer={correctAnswer}
										id={key}
										currentRow={i}
										rows={rows}
										prefix={prefix}
										columns={columns}
									/>
								);
							})}
							<TableRow>
								<TableCell colSpan={columns.length + 1} padding={"none"}>
									<IconButton onClick={onAddRows}>
										<Add />
									</IconButton>
									<IconButton onClick={onRemoveRows} disabled={rows.length <= 1}>
										<Remove />
									</IconButton>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
			<RenderConfigView prefix={prefix} columns={columns.length} rows={rows.length} />
		</Grid>
	);
}

function RenderConfigRow({
	id,
	currentRow,
	rows,
	prefix,
	columns,
	correctAnswer,
	answerConfig,
	onChange,
	onConfigChange,
}: {
	currentRow: number;
	rows: string[];
	prefix: string;
	columns: string[];
	id: string;
	correctAnswer: string[];
	answerConfig: AnswerConfig[];
	onChange: (v: string, i: number) => void;
	onConfigChange: (v: AnswerConfig, i: number) => void;
}) {
	return (
		<TableRow>
			{columns.map((key, k) => {
				return (
					<TableCell
						key={key + id}
						sx={(theme) => ({ padding: theme.spacing(2, 1), borderRight: "1px solid #e0e0e0" })}
						padding={"none"}
					>
						<Grid container wrap={"nowrap"} gap={2}>
							<Grid item>
								<AnswerConfigInput
									rows={rows.length}
									prefix={prefix}
									answerConfig={answerConfig}
									onConfigChange={onConfigChange}
									columns={columns.length}
									currentRow={currentRow}
									currentColumn={k}
								/>
							</Grid>
							<Grid item>
								<CorrectAnswerInput
									rows={rows.length}
									onChange={onChange}
									correctAnswer={correctAnswer}
									prefix={prefix}
									columns={columns.length}
									currentRow={currentRow}
									currentColumn={k}
								/>
							</Grid>
						</Grid>
					</TableCell>
				);
			})}
			<TableCell />
		</TableRow>
	);
}

function AnswerConfigInput({
	currentColumn,
	currentRow,
	answerConfig,
	rows,
	onConfigChange,
}: {
	rows: number;
	answerConfig: AnswerConfig;
	currentRow: number;
	currentColumn: number;
	onConfigChange: (v: AnswerConfig, i: number) => void;
}) {
	useEffect(() => {
		if (!answerConfig[rows * currentColumn + currentRow])
			onConfigChange({ label: "", ypos: currentRow + 1, xpos: currentColumn + 1 }, rows * currentColumn + currentRow);
	}, []);

	useEffect(() => {
		if (answerConfig[rows * currentColumn + currentRow]) {
			onConfigChange(
				{ label: answerConfig[rows * currentColumn + currentRow].label, ypos: currentRow + 1, xpos: currentColumn + 1 },
				rows * currentColumn + currentRow
			);
		} else {
			onConfigChange({ label: "", ypos: currentRow + 1, xpos: currentColumn + 1 }, rows * currentColumn + currentRow);
		}
	}, [rows, currentColumn, currentRow]);

	return (
		<TextField
			value={answerConfig[rows * currentColumn + currentRow]?.label}
			onChange={(e) =>
				onConfigChange(
					{ label: e.target.value, ypos: currentRow + 1, xpos: currentColumn + 1 },
					rows * currentColumn + currentRow
				)
			}
			sx={{ minWidth: "120px" }}
			size={"small"}
			label={`Заголовок №${rows * currentColumn + currentRow + 1}`}
		/>
	);
}

function CorrectAnswerInput({
	currentColumn,
	currentRow,
	rows,
	correctAnswer,
	onChange,
}: {
	rows: number;
	currentRow: number;
	currentColumn: number;
	correctAnswer: string[];
	onChange: (v: string, i: number) => void;
}) {
	useEffect(() => {
		if (!correctAnswer[rows * currentColumn + currentRow]) onChange("", rows * currentColumn + currentRow);
		return () => onChange("", rows * currentColumn + currentRow);
	}, []);

	useEffect(() => {
		if (correctAnswer[rows * currentColumn + currentRow]) {
			onChange(correctAnswer[rows * currentColumn + currentRow], rows * currentColumn + currentRow);
		} else {
			onChange("", rows * currentColumn + currentRow);
		}
	}, [rows, currentColumn, currentRow]);

	return (
		<TextField
			value={correctAnswer[rows * currentColumn + currentRow]}
			onChange={(e) => onChange(e.target.value, rows * currentColumn + currentRow)}
			sx={{ minWidth: "120px" }}
			size={"small"}
			label={`Верный ответ №${rows * currentColumn + currentRow + 1}`}
		/>
	);
}

function RenderConfigView({ rows, columns, prefix = "" }: { rows: number; columns: number; prefix?: string }) {
	const { getValues } = useFormContext();

	return (
		<TableContainer sx={{ maxWidth: 250 }}>
			<Table>
				<TableBody>
					{new Array(rows).fill(null).map((_, y) => {
						return (
							<TableRow key={y}>
								{new Array(columns).fill(null).map((_, x) => {
									return <ViewCell key={x} value={getValues(`${prefix}correct_answer.${rows * x + y}`)} />;
								})}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

function ViewCell({ value }: { value: string }) {
	const ref = useRef(null);
	useKatex(ref, [value]);

	return (
		<TableCell sx={{ border: "1px solid #e0e0e0" }} ref={ref}>
			{value}
		</TableCell>
	);
}
